<template>
  <div class="booking-page">
    <div class="wrap">
      <div v-if="loading" class="state">Loading your booking...</div>

      <div v-else-if="!booking" class="state card">
        <h2>We couldn't find that booking.</h2>
        <p>{{ error || 'The link may be incomplete. Try the one in your confirmation email.' }}</p>
        <RouterLink class="btn-primary" to="/author-nation">Back to Author Nation</RouterLink>
      </div>

      <template v-else-if="booking.status === 'booked'">
        <div class="check">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M5 12l5 5L20 7" />
          </svg>
        </div>
        <h1>You're booked.</h1>
        <p class="sub">See you in Vegas. We'll send a reminder before your time.</p>

        <div class="card details">
          <div class="row">
            <span class="k">When</span>
            <span class="v">
              <strong>{{ longDayLabel(booking.startsAt) }}</strong>
              {{ timeLabel(booking.startsAt) }} Las Vegas time (PT)
            </span>
          </div>
          <div class="row">
            <span class="k">How long</span>
            <span class="v">15 minutes with a Twig editor</span>
          </div>
          <div class="row">
            <span class="k">Where</span>
            <span class="v">
              <strong>{{ booking.location }}</strong>
              {{ booking.address }}
            </span>
          </div>
          <div class="buttons">
            <a class="btn-primary" :href="booking.icsUrl">Add to calendar</a>
            <RouterLink class="btn-secondary" :to="rescheduleLink">Change time</RouterLink>
          </div>
          <div class="cancel">
            <template v-if="!confirmingCancel">
              <button class="text-btn" @click="confirmingCancel = true">Cancel booking</button>
            </template>
            <template v-else>
              <span>Cancel this consult?</span>
              <button class="text-btn danger" :disabled="working" @click="cancel">Yes, cancel it</button>
              <button class="text-btn" @click="confirmingCancel = false">Keep it</button>
            </template>
          </div>
          <p v-if="error" class="error">{{ error }}</p>
        </div>

        <h2 class="h2">Before you come</h2>
        <ol class="tips">
          <li><strong>Bring your questions.</strong> What's the one thing you're most unsure about in this chapter?</li>
          <li><strong>Skim your roast.</strong> It's a good place to start the conversation.</li>
          <li><strong>Come find us at booth A7</strong> a couple of minutes before your time.</li>
        </ol>
        <RouterLink v-if="booking.projectId" class="link" :to="`/edit/${booking.projectId}`"
          >Read your roast →</RouterLink
        >
      </template>

      <template v-else-if="booking.status === 'cancelled' || booking.status === 'no_show'">
        <h1>{{ booking.status === 'cancelled' ? 'Booking cancelled.' : 'We missed you.' }}</h1>
        <p class="sub">Want a different time? There may still be room this week.</p>
        <RouterLink class="btn-primary" :to="newBookingLink">Pick a new time</RouterLink>
      </template>

      <template v-else>
        <h1>Thanks for stopping by.</h1>
        <p class="sub">We hope the consult helped. Your roast is still here whenever you want it.</p>
        <RouterLink v-if="booking.projectId" class="btn-primary" :to="`/edit/${booking.projectId}`">
          Read your roast
        </RouterLink>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { consultApi, longDayLabel, timeLabel, type ConsultBooking } from '../lib/consults';

const route = useRoute();
const id = route.params['id'] as string;
const token = (route.query['token'] as string) || undefined;

const booking = ref<ConsultBooking | null>(null);
const loading = ref(true);
const working = ref(false);
const confirmingCancel = ref(false);
const error = ref('');

const rescheduleLink = computed(() => ({
  path: '/author-nation/book',
  query: { reschedule: id, token: booking.value?.token, project: booking.value?.projectId || undefined },
}));
const newBookingLink = computed(() => ({
  path: '/author-nation/book',
  query: { project: booking.value?.projectId || undefined },
}));

async function cancel() {
  working.value = true;
  error.value = '';
  try {
    booking.value = (await consultApi.cancel(id, token)).booking;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Something broke. Try again in a sec.';
  } finally {
    working.value = false;
    confirmingCancel.value = false;
  }
}

onMounted(async () => {
  document.title = 'Your consult | Twig';
  try {
    booking.value = (await consultApi.get(id, token)).booking;
  } catch (e) {
    error.value = e instanceof Error ? e.message : '';
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.booking-page {
  flex: 1;
  min-height: 100%;
  background: #fff;
  padding: 48px 16px 80px;
}
.wrap {
  max-width: 600px;
  margin: 0 auto;
}
.check {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #f2faf5;
  color: var(--color-success);
  display: grid;
  place-items: center;
  margin-bottom: 16px;
}
h1 {
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
}
.sub {
  color: var(--color-text-secondary);
  font-size: 16px;
  margin: 6px 0 24px;
}
.card {
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  padding: 8px 24px 20px;
}
.state.card {
  padding: 28px;
}
.state.card h2 {
  margin: 0 0 6px;
  font-size: 20px;
}
.row {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--color-border-primary);
}
.k {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  padding-top: 2px;
}
.v {
  display: flex;
  flex-direction: column;
  color: var(--color-text-secondary);
}
.v strong {
  color: var(--color-text-primary);
  font-size: 16px;
}
.buttons {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
}
.btn-primary,
.btn-secondary {
  display: inline-block;
  padding: 12px 22px;
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
}
.btn-primary {
  background: var(--color-accent);
  color: #fff;
}
.btn-primary:hover {
  background: var(--color-accent-hover);
}
.btn-secondary {
  border: 1px solid var(--color-border-secondary);
  color: var(--color-text-primary);
  background: #fff;
}
.btn-secondary:hover {
  background: var(--color-bg-secondary);
}
.cancel {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 14px;
  font-size: 13px;
  color: var(--color-text-secondary);
}
.text-btn {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--color-text-muted);
  text-decoration: underline;
  cursor: pointer;
}
.text-btn.danger {
  color: var(--color-danger);
}
.h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 36px 0 12px;
}
.tips {
  margin: 0 0 20px;
  padding-left: 20px;
  color: var(--color-text-secondary);
  display: grid;
  gap: 8px;
}
.tips strong {
  color: var(--color-text-primary);
}
.link {
  color: var(--color-accent-hover);
  font-weight: 500;
  text-decoration: none;
}
.error {
  color: var(--color-danger);
  font-size: 13px;
  margin: 10px 0 0;
}
.state {
  color: var(--color-text-secondary);
}
@media (max-width: 520px) {
  h1 {
    font-size: 26px;
  }
  .row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .card {
    padding: 4px 18px 18px;
  }
}
</style>
